import { Flex } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AnyTask, Option, Point, SortOrder } from "tm-shared/types";

import { SORT_OPTIONS } from "@/constants/ui";
import {
    EmptyPoints,
    PointHeaderView,
    useFilteredPoints,
} from "@/domains/point";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
    ItemListView,
    SearchAndSortControls,
    UniversalTabs,
} from "@/shared/ui";
import {
    patchProductsSelectedFilters,
    setPointsSortOption,
    setProductsSortOrder,
    setProductsSortTag,
    setWorkPlanActiveCollapse,
    setWorkPlanQuery,
} from "@/store/slices/ui";

import { PointBody } from "./components/PointBody";

type WorkPlanTab = "general" | "extra" | "completed";

const TABS_CONFIG = [
    { label: "Основні", value: "general" as const },
    { label: "Позапланові", value: "extra" as const },
    { label: "Завершені", value: "completed" as const },
] as const;

const EMPTY_MESSAGES: Record<WorkPlanTab, string> = {
    general: "Всі основні точки виконано. Чудова робота!",
    extra: "Позапланових точок поки що немає",
    completed: "Завершених точок поки що немає",
};

interface PointsSectionProps {
    isWorking: boolean;
    onOpenTasksInfoModal: (params: { point: Point; task: AnyTask }) => void;
}

export const PointsSection = ({
    isWorking,
    onOpenTasksInfoModal,
}: PointsSectionProps) => {
    const dispatch = useAppDispatch();
    const [activeTab, setActiveTab] = useState<WorkPlanTab>("general");

    const points = useAppSelector((s) => s.work.points);
    const tasks = useAppSelector((s) => s.work.tasks);
    const pointExtraIds = useAppSelector((s) => s.work.pointExtraIds);
    const { pointsSortOption, query, activeCollapse } = useAppSelector(
        (s) => s.ui.workPlan,
    );

    const {
        filteredPoints: generalPoints,
        filteredExtraPoints: extraPoints,
        filteredCompletedPoints: completedPoints,
    } = useFilteredPoints({
        points,
        tasks,
        pointExtraIds,
        searchTerm: query,
        selectedSortOption: pointsSortOption,
    });

    const pointsByTab = useMemo(
        () => ({
            general: generalPoints,
            extra: extraPoints,
            completed: completedPoints,
        }),
        [generalPoints, extraPoints, completedPoints],
    );

    const tabsWithCounts = useMemo(
        () =>
            TABS_CONFIG.map((tab) => ({
                ...tab,
                count: pointsByTab[tab.value].length,
            })),
        [pointsByTab],
    );

    useEffect(() => {
        if (!activeCollapse) return;

        if (generalPoints.some((point) => point.id === activeCollapse)) {
            setActiveTab("general");
            return;
        }

        if (extraPoints.some((point) => point.id === activeCollapse)) {
            setActiveTab("extra");
            return;
        }

        if (completedPoints.some((point) => point.id === activeCollapse)) {
            setActiveTab("completed");
        }
    }, [activeCollapse, generalPoints, extraPoints, completedPoints]);

    const handleChangeTab = (tab: string) => {
        dispatch(setWorkPlanActiveCollapse(null));
        setActiveTab(tab as WorkPlanTab);
    };

    const currentPoints = pointsByTab[activeTab];

    const handleChangeSort = useCallback(
        (option: Option) => {
            dispatch(setPointsSortOption(option));
        },
        [dispatch],
    );

    const handleChangeQuery = useCallback(
        (value: string) => dispatch(setWorkPlanQuery(value)),
        [dispatch],
    );

    const onChangeCollapse = (newActiveKey: string | null) => {
        dispatch(setWorkPlanActiveCollapse(newActiveKey));
        dispatch(patchProductsSelectedFilters({}));
        dispatch(setProductsSortOrder(SortOrder.Asc));
        dispatch(setProductsSortTag(null));
    };

    if (!isWorking) {
        return null;
    }

    return (
        <>
            <UniversalTabs
                tabs={tabsWithCounts}
                value={activeTab}
                onChange={handleChangeTab}
                maxWidth="400px"
            />

            <SearchAndSortControls
                currentSortOption={pointsSortOption}
                sortOptions={SORT_OPTIONS}
                onChangeSort={handleChangeSort}
                query={query}
                onChangeQuery={handleChangeQuery}
                searchPlaceholder={"Введіть назву чи адресу"}
            />

            {currentPoints.length ? (
                <Flex vertical gap={16}>
                    <ItemListView
                        items={currentPoints}
                        activeCollapse={activeCollapse}
                        onChangeCollapse={onChangeCollapse}
                        HeaderComponent={PointHeaderView}
                        BodyComponent={PointBody}
                        bodyProps={{ onOpenTasksInfoModal }}
                    />
                </Flex>
            ) : (
                <EmptyPoints text={EMPTY_MESSAGES[activeTab]} />
            )}
        </>
    );
};
